import { c as _c } from "react/compiler-runtime";
import { translateInline } from '@/globals/translate/translateInline';
import { produce } from '@/globals/helpers/immer';
import { useCallback, useId, useState } from 'react';
import { Form, Fieldset, QuestionTitle, StackAnswers, AnswerLabel, FormError, GetHasUnansweredQuestionsContext, GetHasUnansweredQuestions, QuestionSubtitle } from '../currentQuestion/CurrentQuestionParts';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { stack } from '@/uiPrimitives/layout/stack';
import { AnyRenderedQuestion, GivenAnswers } from '../question.types';
import { MatrixGivenAnswer, RenderedMatrixQuestion } from './matrixMethods.types';
import { AnswerHelp, AnswerHelpButton, QuestionHelp } from '../currentQuestion/Help';
import { getHasUnansweredQuestions } from '../../_data/filterQuestions';
import { QuestionMethods } from '../questionMethods.types';
import { Labeled } from '@/uiComposites/form/Labeled';
import { focusWrapper } from '@/uiPrimitives/base/focus.css';
import { box } from '@/uiPrimitives/layout/box';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { QuestionNumber } from '../AnsweredQuestionParts';
import { interactive } from '@/uiPrimitives/interactive/interactive';
import { radioCSS } from '@/uiComposites/form/radio.css';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { columnCount, matrixColumnBorder, matrixColumnsGrid } from './matrix.css';
import { assignInlineVars } from '@vanilla-extract/dynamic';
const getHasSomePossibleUnansweredQuestions = ({
  question,
  givenAnswers,
  allQuestions
}: {
  question: RenderedMatrixQuestion;
  givenAnswers: GivenAnswers;
  allQuestions: Array<AnyRenderedQuestion>;
}) => {
  const givenAnswersWithAllPossibleAnswers: Array<GivenAnswers> = [];
  let currentQuestionPossibleAnswers: Array<{
    rows: Array<{
      rowId: string;
      columnId: string;
    }>;
  }> = [{
    rows: []
  }];
  for (const row of question.rows) {
    const newResult = [];
    for (const combo of currentQuestionPossibleAnswers) {
      for (const col of question.columns) {
        newResult.push({
          rows: [...combo.rows, {
            rowId: row.id,
            columnId: col.id
          }]
        });
      }
    }
    currentQuestionPossibleAnswers = newResult;
  }
  for (const possibleMatrixAnswer of currentQuestionPossibleAnswers) {
    givenAnswersWithAllPossibleAnswers.push(produce(givenAnswers, givenAnswers => {
      givenAnswers.matrix.set(question.id, possibleMatrixAnswer);
    }));
  }
  const hasSomePossibleUnansweredQuestions = givenAnswersWithAllPossibleAnswers.some(givenAnswersWithAllCurrentAnswers => {
    return getHasUnansweredQuestions(allQuestions, givenAnswersWithAllCurrentAnswers);
  });
  return hasSomePossibleUnansweredQuestions;
};
const getMatrixHasUnansweredQuestions = ({
  allQuestions,
  givenAnswers,
  question,
  currentGivenMatrixAnswers
}: {
  allQuestions: Array<AnyRenderedQuestion>;
  givenAnswers: GivenAnswers;
  question: RenderedMatrixQuestion;
  currentGivenMatrixAnswers: MatrixGivenAnswer;
}) => {
  const hasSomePossibleUnansweredQuestions = getHasSomePossibleUnansweredQuestions({
    question,
    givenAnswers,
    allQuestions
  });
  const speculativeGivenAnswers = produce(givenAnswers, givenAnswers => {
    givenAnswers.matrix.set(question.id, currentGivenMatrixAnswers);
  });
  const hasUnansweredQuestions = getHasUnansweredQuestions(allQuestions, speculativeGivenAnswers);
  if (!hasSomePossibleUnansweredQuestions) {
    return hasSomePossibleUnansweredQuestions;
  }
  return hasUnansweredQuestions;
};
export const CurrentQuestionMatrix: QuestionMethods<RenderedMatrixQuestion, MatrixGivenAnswer>['Current'] = t0 => {
  const $ = _c(61);
  const {
    question,
    givenAnswers,
    onSubmit,
    containerId,
    titleId,
    progressSlot,
    buttonsSlot
  } = t0;
  let t1;
  if ($[0] !== givenAnswers.matrix || $[1] !== question.columns || $[2] !== question.id || $[3] !== question.rows) {
    t1 = givenAnswers.matrix.get(question.id) ?? {
      rows: question.rows.map(row => ({
        rowId: row.id,
        columnId: question.columns[0]?.id
      }))
    };
    $[0] = givenAnswers.matrix;
    $[1] = question.columns;
    $[2] = question.id;
    $[3] = question.rows;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const givenMatrixAnswers = t1;
  const errorId = useId();
  const [currentGivenMatrixAnswers, setCurrentGivenMatrixAnswers] = useState(givenMatrixAnswers);
  let t2;
  if ($[5] !== currentGivenMatrixAnswers || $[6] !== question) {
    t2 = t3 => {
      const {
        allQuestions,
        givenAnswers: givenAnswers_0
      } = t3;
      return getMatrixHasUnansweredQuestions({
        allQuestions,
        givenAnswers: givenAnswers_0,
        question,
        currentGivenMatrixAnswers
      });
    };
    $[5] = currentGivenMatrixAnswers;
    $[6] = question;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const getHasUnansweredQuestions = t2;
  let t3;
  if ($[8] !== currentGivenMatrixAnswers || $[9] !== onSubmit || $[10] !== question.columns || $[11] !== question.id || $[12] !== question.rows) {
    t3 = () => {
      onSubmit({
        givenAnswersProducer: draft => {
          draft.matrix.set(question.id, currentGivenMatrixAnswers);
        },
        getAnswerTitle: () => question.rows.map(t4 => {
          const {
            id
          } = t4;
          const columnId = currentGivenMatrixAnswers.rows.find(row_0 => id === row_0.rowId)?.columnId;
          return `${question.rows.find(row_1 => row_1.id === id)?.titleText}:${question.columns.find(col => col.id === columnId)?.titleText || ""}`;
        }).join(";")
      });
    };
    $[8] = currentGivenMatrixAnswers;
    $[9] = onSubmit;
    $[10] = question.columns;
    $[11] = question.id;
    $[12] = question.rows;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classes(stack({
      rowGap: "3 | h1"
    }));
    t5 = <QuestionNumber />;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  let t6;
  if ($[16] !== question.title || $[17] !== titleId) {
    t6 = <QuestionTitle id={titleId}>{question.title}</QuestionTitle>;
    $[16] = question.title;
    $[17] = titleId;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== question.help || $[20] !== question.id || $[21] !== question.titleText || $[22] !== t6) {
    t7 = <QuestionHelp help={question.help} titleText={question.titleText} questionId={question.id}>{t6}</QuestionHelp>;
    $[19] = question.help;
    $[20] = question.id;
    $[21] = question.titleText;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== question.subtitle) {
    t8 = Boolean(question.subtitle) && <QuestionSubtitle>{question.subtitle}</QuestionSubtitle>;
    $[24] = question.subtitle;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== currentGivenMatrixAnswers || $[27] !== errorId || $[28] !== question.columns || $[29] !== question.id || $[30] !== question.rows || $[31] !== question.titleText) {
    let t10;
    if ($[33] !== currentGivenMatrixAnswers || $[34] !== errorId || $[35] !== question.columns || $[36] !== question.id || $[37] !== question.titleText) {
      t10 = row_2 => {
        const currentGivenAnswerForRow = currentGivenMatrixAnswers.rows.find(rowData => rowData.rowId === row_2.id)?.columnId;
        return <Labeled.Container key={row_2.id}><div className={classes(stack({
            rowGap: "1 | h3"
          }))}><AnswerHelp help={row_2.help}><div className={classes(textStyle({
                fontWeight: "bold"
              }))}><AnswerLabel helpSlot={Boolean(row_2.help) && <AnswerHelpButton titleText={row_2.titleText} questionTitle={question.titleText} currentQuestionId={question.id} />}>{row_2.title}</AnswerLabel></div></AnswerHelp><div className={classes(matrixColumnsGrid)} style={assignInlineVars({
              [columnCount]: String(question.columns.length)
            })}>{question.columns.map((column, index) => {
                const isChecked = currentGivenAnswerForRow === column.id || currentGivenAnswerForRow == null && index === 0;
                return <Labeled.Container key={`${row_2.id}-${column.id}`}><Labeled.Label className={classes(focusWrapper, interactive({
                    cursor: "pointer"
                  }), box({
                    padding: "1 | h3",
                    backgroundColor: "negative",
                    borderStyle: "solid"
                  }), matrixColumnBorder, shelf({
                    gridTemplateColumns: "auto minmax(0, 1fr)"
                  }))}><span className={classes(occupy({
                      inlineSize: indentSize,
                      blockSize: "capSize",
                      justifyItems: "start"
                    }))}><Labeled.Input type="radio" required={true} name={row_2.id} checked={isChecked} className={classes(radioCSS)} onChange={t11 => {
                        const {
                          currentTarget
                        } = t11;
                        setCurrentGivenMatrixAnswers(produce(draft_0 => {
                          const index_0 = draft_0?.rows.findIndex(t12 => {
                            const {
                              rowId
                            } = t12;
                            return rowId === row_2.id;
                          });
                          if (index_0 !== -1 && currentTarget.checked) {
                            draft_0.rows[index_0].columnId = column.id;
                          } else {
                            draft_0.rows.push({
                              rowId: row_2.id,
                              columnId: column.id
                            });
                          }
                        }));
                      }} aria-describedby={errorId} /></span><span className={classes(textBlock({}))}>{column.title}</span></Labeled.Label></Labeled.Container>;
              })}</div></div></Labeled.Container>;
      };
      $[33] = currentGivenMatrixAnswers;
      $[34] = errorId;
      $[35] = question.columns;
      $[36] = question.id;
      $[37] = question.titleText;
      $[38] = t10;
    } else {
      t10 = $[38];
    }
    t9 = question.rows.map(t10);
    $[26] = currentGivenMatrixAnswers;
    $[27] = errorId;
    $[28] = question.columns;
    $[29] = question.id;
    $[30] = question.rows;
    $[31] = question.titleText;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[39] !== t9) {
    t10 = <StackAnswers>{t9}</StackAnswers>;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = translateInline({
      en: "Please select an answer for each row",
      nl: "Selecteer antwoord voor elke rij"
    });
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== errorId) {
    t12 = <FormError id={errorId}>{t11}</FormError>;
    $[42] = errorId;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== t10 || $[45] !== t12 || $[46] !== t7 || $[47] !== t8 || $[48] !== titleId) {
    t13 = <Fieldset aria-labelledby={titleId}>{t7}{t8}{t10}{t12}</Fieldset>;
    $[44] = t10;
    $[45] = t12;
    $[46] = t7;
    $[47] = t8;
    $[48] = titleId;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== progressSlot || $[51] !== t13) {
    t14 = <div className={t4}>{t5}{progressSlot}{t13}</div>;
    $[50] = progressSlot;
    $[51] = t13;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== buttonsSlot || $[54] !== containerId || $[55] !== t14 || $[56] !== t3) {
    t15 = <Form id={containerId} onSubmit={t3}>{t14}{buttonsSlot}</Form>;
    $[53] = buttonsSlot;
    $[54] = containerId;
    $[55] = t14;
    $[56] = t3;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== getHasUnansweredQuestions || $[59] !== t15) {
    t16 = <GetHasUnansweredQuestionsContext.Provider value={getHasUnansweredQuestions}>{t15}</GetHasUnansweredQuestionsContext.Provider>;
    $[58] = getHasUnansweredQuestions;
    $[59] = t15;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  return t16;
};